
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { connect } from "react-redux";

import { Alert, Dialog, Table } from "../../layout";
import AddEditCat from "./AddEditCat";

import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { safeOnComplete } from "../../utils/safeOnComplete";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Expense Category Details
//------------------------------------------------
const ExpenseCategoryDetails = ({ category, t }) => (
  <Box>
    <Typography
      variant="h5"
      sx={{
        textAlign: "center",
        fontWeight: "bold",
        mb: 3,
      }}
    >
      {t("expenseCategoryDetails")}
    </Typography>

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>
        {t("expenseName")}:
      </Typography>

      <Typography
        sx={{
          textAlign: "right",
          overflowWrap: "anywhere",
        }}
      >
        {category?.name || "-"}
      </Typography>
    </Box>

    <Divider />

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>
        {t("description")}:
      </Typography>

      <Typography
        sx={{
          textAlign: "right",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {category?.descr || "-"}
      </Typography>
    </Box>

    <Divider />
  </Box>
);

//------------------------------------------------
// Expense Categories
//------------------------------------------------
const ExpnsCat = (props) => {
  const { t } = useTranslation();

  const { _list, _get, _clear, _delete } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [updData, setUpdData] = useState();

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Expense Categories
  //------------------------------------------------
  useEffect(() => {
    setLoader(true);

    _get(
      endpoints.ExpenseCat,
      actions.ActionType.SetExpenseCats,
      () => setLoader(false)
    );

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //------------------------------------------------
  // Add / Edit
  //------------------------------------------------
  const handleAddEdit = (val) => {
    setUpdData(val);
    setShowForm(true);
  };

  //------------------------------------------------
  // View
  //------------------------------------------------
  const handleView = (data) => {
    const originalCategory = (_list || []).find(
      (item) => Number(item.id) === Number(data.id)
    );

    setViewData(originalCategory || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Delete
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteExpenseCategoryConfirmation"),

      onConfirm: () => {
        const onComplete = safeOnComplete(
          (res) => {
            setAlert({
              isOpen: true,
              type:
                res.status === 200
                  ? "success"
                  : "warning",
              title:
                res.status === 200
                  ? t("deletedSuccessfully")
                  : t("warning"),
              subTitle: res.msg,
            });
          },
          (err) => {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: err.msg,
            });
          }
        );

        _delete(
          endpoints.ExpenseCat,
          id,
          actions.ActionType.DeleteExpenseCat,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Data and Columns
  //------------------------------------------------
  const tableData = Array.isArray(_list) ? _list : [];

  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "name", label: t("expenseName") },
    { id: "descr", label: t("description") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editExpenseCategory")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteExpenseCategory")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Expense Category Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addExpenseCategory")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("expenseCategories")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("expenseCategories")}
        />
      </Box>

      {/* Normal Screen Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Expense Categories List */}
      <Box
        ref={reportRef}
        className="expense-categories-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("expenseCategories")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Add / Edit Dialog */}
      {showForm && (
        <Dialog show={showForm} onClose={false}>
          <AddEditCat
            updData={updData}
            setShowForm={setShowForm}
          />
        </Dialog>
      )}

      {/* View Expense Category Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            {/* Print Preview */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("expenseCategoryDetails")}
              />
            </Box>

            {/* Screen Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ExpenseCategoryDetails
                category={viewData}
                t={t}
              />
            </Box>

            {/* Printable Details */}
            <Box
              ref={detailsRef}
              className="expense-category-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ExpenseCategoryDetails
                category={viewData}
                t={t}
              />
            </Box>

            {/* Close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.expenseCategories,
});

const mapActionToProps = {
  _get: actions.get,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(ExpnsCat);
